/**
 * The right-panel surface model: what kinds exist, what a surface looks like,
 * and how one is constructed.
 *
 * A leaf module on purpose. The store needs the model to hold state, and the
 * persistence validator needs it to check state read back from disk — so if
 * either owned it, the two would import each other. They did, until this was
 * split out.
 */
import type {
  EnvironmentId,
  ProjectId,
  SourceControlProviderKind,
  TurnId,
} from "@cmmd-forge/contracts";

/**
 * Every surface kind the right panel can host.
 *
 * t3code's full list: `terminal` hosts the thread's shells beside the
 * transcript instead of only in the drawer (which stays), and `files` lists
 * the workspace tree. A kind with no renderer is dead code and a lie about
 * what the app can do.
 */
export const RIGHT_PANEL_KINDS = [
  "preview",
  "terminal",
  "canvas",
  "diff",
  "plan",
  "task",
  "simulator",
  "display",
  "agents",
  "file",
  "files",
  "pull-request",
] as const;
export type FileReadPolicy = "connected-only" | "execution-intent";

export type RightPanelKind = (typeof RIGHT_PANEL_KINDS)[number];

/** Kinds that can only ever have one instance open at a time. */
export type SingletonRightPanelKind = Exclude<RightPanelKind, "preview" | "file" | "pull-request">;

/**
 * Singletons that carry no state of their own.
 *
 * `diff` is a singleton too, but it has its own union member because it holds
 * the turn and file it is showing. Leaving it in this alias as well would make
 * the two members overlap, and an overlapping union does not discriminate —
 * `surface.kind === "diff"` would not narrow to the member with `turnId` on it.
 */
type StatelessSingletonKind = Exclude<SingletonRightPanelKind, "diff">;

type RightPanelSurfaceMembers =
  /** A browser session. `browser:new` is the not-yet-navigated placeholder tab. */
  | { id: `browser:${string}`; kind: "preview"; resourceId: string }
  | { id: "browser:new"; kind: "preview"; resourceId: null }
  /**
   * The diff surface carries the turn and file it is showing. That sub-state
   * used to live in the URL as `diffTurnId` / `diffFilePath`; it belongs to this
   * surface, so it moves here with it.
   */
  | { id: "diff"; kind: "diff"; turnId: TurnId | null; filePath: string | null }
  /**
   * One open file. Several can be open as peer tabs, which is why the path is
   * in the id. `revealRequestId` increments on every re-open of the same path so
   * the viewer can distinguish "reveal line 42 again" from a re-render.
   */
  | {
      id: `file:${string}`;
      kind: "file";
      relativePath: string;
      revealLine: number | null;
      revealRequestId: number;
      readPolicy: FileReadPolicy;
    }
  | PullRequestRightPanelSurface
  | { id: StatelessSingletonKind; kind: StatelessSingletonKind };

/**
 * A surface, plus the name the user gave its tab.
 *
 * `label` is the rename override: absent until the user names the tab, and
 * cleared when the rename is blank, so a tab always falls back to its derived
 * label. Deliberately distinct from the pull-request surface's `title`, which
 * is the server's own name for the change request.
 */
export type RightPanelSurface = RightPanelSurfaceMembers & { readonly label?: string };

export interface PullRequestSurfaceInput {
  readonly provider: SourceControlProviderKind;
  readonly environmentId: EnvironmentId;
  readonly repositoryLinkId: string | null;
  readonly projectId: ProjectId;
  readonly host: string;
  readonly repository: string;
  readonly number: number;
  readonly title?: string | null;
}

export interface PullRequestRightPanelSurface extends PullRequestSurfaceInput {
  readonly id: `pull-request:${string}`;
  readonly kind: "pull-request";
}

export interface ThreadRightPanelState {
  readonly isOpen: boolean;
  /**
   * The panel is taking the whole window, hiding the transcript. Layout, not
   * content — switching tabs while maximized keeps the layout.
   */
  readonly isMaximized?: boolean;
  readonly activeSurfaceId: string | null;
  readonly surfaces: readonly RightPanelSurface[];
}

export const EMPTY_THREAD_STATE: ThreadRightPanelState = {
  isOpen: false,
  isMaximized: false,
  activeSurfaceId: null,
  surfaces: [],
};

export function browserSurface(tabId: string | null): RightPanelSurface {
  return tabId === null
    ? { id: "browser:new", kind: "preview", resourceId: null }
    : { id: `browser:${tabId}`, kind: "preview", resourceId: tabId };
}

export function fileSurface(
  relativePath: string,
  revealLine: number | null,
  revealRequestId: number,
  readPolicy: FileReadPolicy = "execution-intent",
): RightPanelSurface {
  return {
    id: `file:${relativePath}`,
    kind: "file",
    relativePath,
    revealLine,
    revealRequestId,
    readPolicy,
  };
}

export function pullRequestSurface(input: PullRequestSurfaceInput): PullRequestRightPanelSurface {
  const identity = [
    input.provider,
    input.environmentId,
    input.repositoryLinkId ?? "",
    input.projectId,
    input.host.toLowerCase(),
    input.repository,
    String(input.number),
  ]
    .map((part) => encodeURIComponent(part))
    .join(":");
  return {
    ...input,
    id: `pull-request:${identity}`,
    kind: "pull-request",
  };
}

export function singletonSurface(kind: SingletonRightPanelKind): RightPanelSurface {
  if (kind === "diff") return { id: "diff", kind: "diff", turnId: null, filePath: null };
  return { id: kind, kind };
}
